import React, { useEffect, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAPI } from '../../../Contexts/APIContext'
import { useInView } from 'react-intersection-observer'
import { formatStringInNav } from '../../../Helpers/String+Extension'
import { Breadcrumbs } from '../../../Components/GeneralComponents/Breadcrumbs/Breadcrumbs'
import { useArtistSongsStore } from '../../../Storage/artistSongsPage/useArtistSongsStore'
import DebounceSearch from '../../../Components/GeneralComponents/DebounceSearch/DebounceSearch'
import SongGridItem from '../../../Components/DashboardComponents/SongGridItem/SongGridItem'
import SongListItem from '../../../Components/DashboardComponents/SongListItem/SongListItem'
import OptionsList from '../../../Components/GeneralComponents/OptionsList/OptionsList'
import Loader from '../../../Components/GeneralComponents/Loader'
import classes from './index.module.css'
import useGetArtistData from '../../../Helpers/hooks/useGetArtistData'
import useClearArtistSearchTerms from '../../../Helpers/hooks/useClearArtistSearchTerms'
import { useCurrentItem } from '../../../Contexts/CurrentItemContext'
import ArtistTopTeaseGridItem from '../../../Components/ArtistProfileComponents/ArtistTopTeaseGridItem'
import { StickyHeader } from '../../../Components/StickyHeader'
import useAdminAccess from '../../../services/hooks/useAdminAccess'

export default function ArtistSongs() {
  const pageLimit = 20
  const { isInternal } = useAdminAccess()
  const navigate = useNavigate()
  const { getArtistSongs, getArtistTeases, getSearchAllSongs } = useAPI()
  const { artist_name } = useParams()
  const [ref, inView] = useInView()
  const { pathname } = useLocation()
  const isTease = pathname.includes('tease')

  const [viewMode, setViewMode] = useState(() =>
    isTease ? 'grid' : (localStorage.getItem('artistSongsViewMode') ?? 'grid')
  )

  function handleSetViewMode(mode) {
    setViewMode(mode)
    localStorage.setItem('artistSongsViewMode', mode)
  }

  const getAll = isTease ? getArtistTeases : getArtistSongs

  const options = useArtistSongsStore(state => state.options)
  const selectedOption = useArtistSongsStore(state => state.selectedOption)
  const setSelectedOption = useArtistSongsStore(state => state.setSelectedOption)

  const syncOptions = useArtistSongsStore(state => state.syncOptions)
  const selectedSyncOption = useArtistSongsStore(state => state.selectedSyncOption)
  const setSelectedSyncOption = useArtistSongsStore(state => state.setSelectedSyncOption)

  const { searchTerm, setSearchTerm, inputValue, setInputValue } =
    useClearArtistSearchTerms({ artistName: artist_name })

  const {
    data: dataArtist,
    isLoading: isLoadingArtist,
    isFetching: isFetchingArtist,
    isError: isErrorArtist,
    error: errorArtist,
  } = useGetArtistData({ artistName: artist_name })

  const { setItem } = useCurrentItem()
  useEffect(() => {
    if (dataArtist) {
      setItem(dataArtist)
    }
  }, [dataArtist, setItem])

  const {
    data,
    isError,
    error,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'artistSongsList',
      artist_name,
      searchTerm,
      selectedOption.id,
      selectedSyncOption.id,
      isTease,
    ],
    queryFn: ({ pageParam = 1 }) => {
      return searchTerm === ''
        ? getAll(artist_name, pageParam, pageLimit, selectedOption.id, selectedSyncOption.id)
        : getSearchAllSongs(searchTerm, pageParam, pageLimit, dataArtist.f_id, isTease ? isTease : null, !isTease)
    },
    getNextPageParam: pageData => {
      const nextPage = pageData.p + 1
      const newPage =
        pageData.p * pageLimit < pageData.songs_count ? nextPage : undefined
      return newPage
    },
    enabled: !!dataArtist && !isLoadingArtist && !isFetchingArtist,
  })

  useEffect(() => {
    if (inView) {
      fetchNextPage()
    }
  }, [inView])

  function handleTableScroll(e) {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop - clientHeight < 300 && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  const isListMode = viewMode === 'list' && !isTease

  return (
    <div className={`${classes.wrapper} ${isListMode ? classes.wrapperList : ''}`}>
      {!isLoading && !!dataArtist && !isLoadingArtist && !isFetchingArtist && (
        <StickyHeader className={isListMode ? classes.stickyHeaderList : ''}>
          <Breadcrumbs
            items={[
              {
                text: 'Artists',
                onClick: () => { navigate('/artists') },
              },
              {
                text: formatStringInNav(dataArtist.name),
                onClick: () => { navigate('/artist/' + artist_name) },
              },
              {
                text: !isTease ? 'Songs' : 'Teases',
                onClick: () => {},
              },
            ]}
          />
          <div className={classes.header}>
            <div>
              <h1 className={classes.title}>{`${!isTease ? 'Songs' : 'Teases'} by ${dataArtist.name}`}</h1>
              <p className={classes.subtitle}>
                {data?.pages[0]
                  ? `Total ${!isTease ? 'Songs' : 'Teases'} ` + data?.pages[0]?.songs_count
                  : ''}
              </p>
            </div>
            <div className={classes.searchSortContainer}>
              {!searchTerm ? (
                <OptionsList
                  options={syncOptions}
                  selectedOption={selectedSyncOption}
                  setSelectedOption={option => setSelectedSyncOption(option)}
                />
              ) : null}
              {!searchTerm ? (
                <OptionsList
                  selectedOption={selectedOption}
                  setSelectedOption={option => setSelectedOption(option)}
                  options={options}
                  disabled={!!searchTerm}
                />
              ) : null}
              <DebounceSearch
                inputValue={inputValue}
                setInputValue={setInputValue}
                searchValue={searchTerm}
                setSearchValue={setSearchTerm}
                placeholder={`Search for ${!isTease ? 'Songs' : 'Teases'}...`}
              />
              {!isTease && isInternal && (
                <div className={classes.viewToggle}>
                  <button
                    className={`${classes.viewBtn} ${viewMode === 'grid' ? classes.viewBtnActive : ''}`}
                    onClick={() => handleSetViewMode('grid')}
                    title="Grid view"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="0" y="0" width="7" height="7" rx="1" />
                      <rect x="9" y="0" width="7" height="7" rx="1" />
                      <rect x="0" y="9" width="7" height="7" rx="1" />
                      <rect x="9" y="9" width="7" height="7" rx="1" />
                    </svg>
                  </button>
                  <button
                    className={`${classes.viewBtn} ${viewMode === 'list' ? classes.viewBtnActive : ''}`}
                    onClick={() => handleSetViewMode('list')}
                    title="List view"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="0" y="1" width="16" height="2" rx="1" />
                      <rect x="0" y="7" width="16" height="2" rx="1" />
                      <rect x="0" y="13" width="16" height="2" rx="1" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </StickyHeader>
      )}

      {!isListMode && (
        <div className={classes.songs}>
          {!isError &&
            data &&
            data.pages.map((page, index) => (
              <React.Fragment key={index}>
                {page.songs.length > 0 ? (
                  page.songs.map(song => (
                    <div key={song.f_id} ref={ref}>
                      {!isTease ? (
                        <SongGridItem
                          key={song.f_id}
                          song={song}
                          onClick={song => { navigate('/song/' + song.f_id) }}
                        />
                      ) : (
                        <ArtistTopTeaseGridItem
                          key={song.f_id}
                          song={song}
                          onClick={song => { navigate('/tease/' + song.f_id) }}
                        />
                      )}
                    </div>
                  ))
                ) : (
                  <h1 className={classes.emptyResultText}>No Results</h1>
                )}
              </React.Fragment>
            ))}
        </div>
      )}

      {isListMode && (
        <div className={classes.tableWrapper} onScroll={handleTableScroll}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.th}>Song</th>
                <th className={classes.th}>Artist</th>
                <th className={`${classes.th} ${classes.center}`}>Lyric Status</th>
                <th className={classes.th}>Published</th>
                <th className={classes.th}>Alt Song ID</th>
                <th className={classes.th}>Artists</th>
                <th className={classes.th}>ISRCs</th>
              </tr>
            </thead>
            <tbody>
              {!isError &&
                data &&
                data.pages.map((page, index) => (
                  <React.Fragment key={index}>
                    {page.songs.map(song => (
                      <SongListItem
                        key={song.f_id}
                        song={song}
                        onClick={song => { navigate('/song/' + song.f_id) }}
                      />
                    ))}
                  </React.Fragment>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {(isError || isErrorArtist) && (
        <h1 className={classes.errorText}>
          Error {error?.status || errorArtist?.status}
        </h1>
      )}
      <Loader
        isLoading={isLoadingArtist || isFetchingArtist || isLoading || isFetchingNextPage}
      />
    </div>
  )
}
